class MultiCollectionCarousel extends HTMLElement {
  connectedCallback() {
    this._cache = {};
    this._activeHandle = null;
    this._tabsDidDrag = false;
    this._tabs = this.querySelectorAll('.md-mcc__tab');
    this._track = this.querySelector('[data-carousel-track]');
    this._dotsContainer = this.querySelector('[data-carousel-dots]');
    this._prevBtn = this.querySelector('[data-carousel-prev]');
    this._nextBtn = this.querySelector('[data-carousel-next]');

    // Initialiser avec la première collection (déjà pré-rendue server-side)
    const firstTab = this._tabs[0];
    if (firstTab) {
      this._activeHandle = firstTab.dataset.collectionHandle;
      this._cache[this._activeHandle] = this._track.innerHTML;
    }

    this._tabs.forEach(tab => {
      tab.addEventListener('click', (e) => this._handleTabClick(e));
    });

    this._prevBtn?.addEventListener('click', () => this._scroll('prev'));
    this._nextBtn?.addEventListener('click', () => this._scroll('next'));

    this._initDots();
    this._bindQuickAdd();
    this._initDrag();
    this._initTabsDrag();

    this._track.addEventListener('scroll', () => this._updateDotsFromScroll(), { passive: true });
  }

  async _handleTabClick(e) {
    // Ignorer le click si c'était en réalité un drag
    if (this._tabsDidDrag) {
      this._tabsDidDrag = false;
      return;
    }

    const tab = e.currentTarget;
    const handle = tab.dataset.collectionHandle;
    if (handle === this._activeHandle) return;

    // Mettre à jour les tabs actifs
    this._tabs.forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    this._activeHandle = handle;

    // Charger depuis cache ou AJAX
    if (this._cache[handle]) {
      this._track.innerHTML = this._cache[handle];
    } else {
      this._track.classList.add('is-loading');
      try {
        // Section Rendering API : ?sections= retourne un objet JSON { "section-id": "html..." }
        const res = await fetch(`/collections/${handle}?sections=md-0326-carousel-products`);
        const json = await res.json();
        const html = json['md-0326-carousel-products'];

        // Extraire la grille de cartes du HTML de la section
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const grid = doc.querySelector('.md-mcc__cards-grid');
        const content = grid ? grid.outerHTML : '<p style="padding:24px;color:#5a5a5a;">Aucun produit trouvé.</p>';

        this._cache[handle] = content;
        this._track.innerHTML = content;
      } catch (err) {
        console.error('[md-mcc] Erreur chargement collection :', err);
      } finally {
        this._track.classList.remove('is-loading');
      }
    }

    // Remettre le scroll au début + re-init dots + re-bind quick add
    this._track.scrollLeft = 0;
    this._initDots();
    this._bindQuickAdd();
  }

  // ---- Carousel scroll (CSS scroll-snap) ----

  _getCardWidth() {
    const firstCard = this._track.querySelector('.md-pcard');
    if (!firstCard) return 0;
    return firstCard.offsetWidth + 20; // card width + gap
  }

  _scroll(dir) {
    const perPage = window.innerWidth >= 1024 ? 3 : 1;
    const step = this._getCardWidth() * perPage;
    this._track.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' });
  }

  // ---- Dots ----

  _initDots() {
    const cards = this._track.querySelectorAll('.md-pcard');
    const perPage = window.innerWidth >= 1024 ? 3 : 1;
    const pages = Math.ceil(cards.length / perPage);

    this._dotsContainer.innerHTML = '';

    for (let i = 0; i < pages; i++) {
      const dot = document.createElement('button');
      dot.className = 'md-mcc__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Page ${i + 1}`);
      dot.addEventListener('click', () => {
        const step = this._getCardWidth() * perPage * i;
        this._track.scrollTo({ left: step, behavior: 'smooth' });
      });
      this._dotsContainer.appendChild(dot);
    }
  }

  _updateDotsFromScroll() {
    const perPage = window.innerWidth >= 1024 ? 3 : 1;
    const cardW = this._getCardWidth();
    if (!cardW) return;

    const pageWidth = cardW * perPage;
    const currentPage = Math.round(this._track.scrollLeft / pageWidth);
    const dots = this._dotsContainer.querySelectorAll('.md-mcc__dot');
    dots.forEach((d, i) => d.classList.toggle('is-active', i === currentPage));
  }

  // ---- Drag souris sur les tabs ----

  _initTabsDrag() {
    const tabsEl = this.querySelector('.md-mcc__tabs');
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    tabsEl.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDragging = true;
      this._tabsDidDrag = false;
      startX = e.pageX;
      startScrollLeft = tabsEl.scrollLeft;
      tabsEl.style.cursor = 'grabbing';
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 5) this._tabsDidDrag = true;
      tabsEl.scrollLeft = startScrollLeft - dx;
    });

    window.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      tabsEl.style.cursor = '';
    });
  }

  // ---- Drag souris sur le carousel ----

  _initDrag() {
    const track = this._track;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDragging = true;
      startX = e.pageX;
      startScrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
      // Désactiver scroll-snap pendant le drag pour un mouvement fluide
      track.style.scrollSnapType = 'none';
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.pageX - startX;
      track.scrollLeft = startScrollLeft - dx;
    });

    window.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.cursor = '';
      track.style.userSelect = '';
      // Réactiver scroll-snap et snapper sur la carte la plus proche
      track.style.scrollSnapType = '';
      const cardW = this._getCardWidth();
      if (cardW) {
        const nearest = Math.round(track.scrollLeft / cardW) * cardW;
        track.scrollTo({ left: nearest, behavior: 'smooth' });
      }
      this._updateDotsFromScroll();
    });
  }

  // ---- Quick add AJAX ----

  _bindQuickAdd() {
    this._track.querySelectorAll('.md-pcard__quickadd-form').forEach(form => {
      // Éviter double-bind
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        const id = form.querySelector('[name="id"]').value;

        if (btn) {
          btn.disabled = true;
          btn.textContent = '...';
        }

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(id), quantity: 1 })
          });

          // Récupérer le panier complet puis notifier le thème via son pubsub
          const cartRes = await fetch('/cart.js');
          const cartJson = await cartRes.json();
          if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
            window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'md-mcc',
              cart: cartJson
            });
          }

          if (btn) btn.textContent = '✓ Ajouté';
          setTimeout(() => {
            if (btn) {
              btn.disabled = false;
              btn.textContent = 'Ajouter au panier';
            }
          }, 1500);
        } catch (err) {
          console.error('[md-mcc] Erreur ajout panier :', err);
          if (btn) {
            btn.disabled = false;
            btn.textContent = 'Ajouter au panier';
          }
        }
      });
    });
  }
}

customElements.define('multi-collection-carousel', MultiCollectionCarousel);
